import {ArrowUpRight, FileCheck2, FileWarning} from "lucide-react";
import {Link} from "react-router-dom";
import {formatDateTime} from "../../../shared/utils/format";
import {getStatusPresentation} from "../statusMachine";
import {useTranslation, i18n} from "../../../shared/i18n";


const FLOW_LABELS = {
    get independent() { return i18n.t("sections.onboarding.p51"); },
    get sales_invite() { return i18n.t("sections.onboarding.p52"); },
};

const displayName = (row) => row.display_name || row.company_name || i18n.t("sections.onboarding.p20", {v0: row.id});

const supportingName = (row) => {
    if (!row.company_name || row.company_name === displayName(row)) return null;
    return row.company_name;
};

const locationLabel = (row) =>
    [row.city_name, row.country_name].filter(Boolean).join(", ") || i18n.t("sections.onboarding.p27");

const flowLabel = (row) => FLOW_LABELS[row.flow_type] || row.flow_type;

const StatusBadge = ({status}) => {
    const presentation = getStatusPresentation(status);
    return (
        <span className={`onboarding-status onboarding-status-${presentation.tone}`}>
            {presentation.label}
        </span>
    );
};

const DocumentsCount = ({row}) => (
    <span className="onboarding-document-count">
        {row.rejected_documents_count > 0 ? (
            <FileWarning aria-hidden="true" size={15} />
        ) : (
            <FileCheck2 aria-hidden="true" size={15} />
        )}
        <span>{row.documents_count}</span>
        {row.rejected_documents_count > 0 ? (
            <span className="onboarding-document-rejected">
                {row.rejected_documents_count} {i18n.t("sections.onboarding.p53")}
            </span>
        ) : null}
    </span>
);

const QueueSkeleton = () => (
    <div className="onboarding-queue-skeleton" role="status" aria-label={i18n.t("sections.onboarding.p54")}>
        <span className="onboarding-sr-only">{i18n.t("sections.onboarding.p54")}</span>
        {Array.from({length: 6}, (_, index) => (
            <span key={index} data-testid="queue-skeleton-row" />
        ))}
    </div>
);

export const ApplicationQueue = ({applications, loading, refreshing}) => {
    const {t} = useTranslation();
    if (loading) return <QueueSkeleton />;

    return (
        <div className="onboarding-queue" aria-busy={refreshing ? "true" : "false"}>
            <div className="onboarding-queue-table-wrap">
                <table
                    className="admin-table onboarding-queue-table"
                    aria-label={t("sections.onboarding.p55")}
                >
                    <caption className="onboarding-sr-only">{t("sections.onboarding.p55")}</caption>
                    <thead>
                        <tr>
                            <th className="onboarding-submitted-column" scope="col">
                                {t("sections.onboarding.p56")}
                            </th>
                            <th scope="col">{t("sections.onboarding.p57")}</th>
                            <th scope="col">{t("sections.onboarding.p58")}</th>
                            <th scope="col">{t("sections.onboarding.p59")}</th>
                            <th scope="col">{t("sections.onboarding.p60")}</th>
                            <th scope="col">{t("sections.onboarding.p61")}</th>
                            <th scope="col">{t("sections.onboarding.p62")}</th>
                            <th scope="col">{t("sections.onboarding.p25")}</th>
                            <th scope="col">
                                <span className="onboarding-sr-only">{t("sections.onboarding.p63")}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {applications.map((row) => (
                            <tr key={row.id}>
                                <td className="onboarding-submitted-column">
                                    <strong>#{row.id}</strong>
                                    <span className="onboarding-cell-secondary">
                                        {formatDateTime(row.submitted_at)}
                                    </span>
                                </td>
                                <td className="onboarding-wrap">
                                    <Link to={`applications/${row.id}`}>{displayName(row)}</Link>
                                    {supportingName(row) ? (
                                        <span className="onboarding-cell-secondary">
                                            {supportingName(row)}
                                        </span>
                                    ) : null}
                                    <span className="onboarding-cell-secondary onboarding-business-submitted">
                                        {formatDateTime(row.submitted_at)} · #{row.id}
                                    </span>
                                </td>
                                <td className="onboarding-wrap">
                                    <strong>{row.applicant_name || i18n.t("sections.onboarding.p27")}</strong>
                                    <span className="onboarding-cell-secondary">
                                        {row.phone_number || row.email || i18n.t("sections.onboarding.p64")}
                                    </span>
                                </td>
                                <td className="onboarding-wrap">{locationLabel(row)}</td>
                                <td className="onboarding-wrap">
                                    {flowLabel(row)}
                                    <span className="onboarding-cell-secondary">
                                        {row.sales_manager_name || i18n.t("sections.onboarding.p65")}
                                    </span>
                                </td>
                                <td>
                                    <DocumentsCount row={row} />
                                </td>
                                <td>
                                    <StatusBadge status={row.status} />
                                </td>
                                <td>{formatDateTime(row.updated_at)}</td>
                                <td className="onboarding-open-cell">
                                    <Link
                                        className="onboarding-open-button"
                                        to={`applications/${row.id}`}
                                        aria-label={i18n.t("sections.onboarding.p66", {v0: row.id})}
                                        title={i18n.t("sections.onboarding.p67")}
                                    >
                                        <ArrowUpRight aria-hidden="true" size={17} />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ul className="onboarding-queue-mobile" aria-label={t("sections.onboarding.p55")}>
                {applications.map((row) => (
                    <li key={row.id}>
                        <div className="onboarding-mobile-heading">
                            <div>
                                <span className="onboarding-mobile-id">
                                    #{row.id} · {formatDateTime(row.submitted_at)}
                                </span>
                                <h2 className="onboarding-mobile-business-name">
                                    {displayName(row)}
                                </h2>
                            </div>
                            <StatusBadge status={row.status} />
                        </div>
                        <dl className="onboarding-mobile-facts">
                            <div>
                                <dt>{i18n.t("sections.onboarding.p58")}</dt>
                                <dd>{row.applicant_name || i18n.t("sections.onboarding.p27")}</dd>
                            </div>
                            <div>
                                <dt>{i18n.t("sections.onboarding.p26")}</dt>
                                <dd>{locationLabel(row)}</dd>
                            </div>
                            <div>
                                <dt>{i18n.t("sections.onboarding.p61")}</dt>
                                <dd>
                                    {row.documents_count}
                                    {row.rejected_documents_count > 0
                                        ? i18n.t("sections.onboarding.p68", {v0: row.rejected_documents_count})
                                        : ""}
                                </dd>
                            </div>
                            <div>
                                <dt>{i18n.t("sections.onboarding.p60")}</dt>
                                <dd>{flowLabel(row)}</dd>
                            </div>
                        </dl>
                        <Link
                            className="admin-button admin-button-secondary onboarding-mobile-open"
                            to={`applications/${row.id}`}
                            aria-label={i18n.t("sections.onboarding.p69", {v0: row.id})}
                        >
                            {i18n.t("sections.onboarding.p63")}
                            <ArrowUpRight aria-hidden="true" size={16} />
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
};
